import {
  acceptHotItemStore,
  defineItemStore
} from "@editor/items/itemStore.store";
import { some } from "lodash-es";

import { useMediaStore } from "@core/media/media.store";

import type { ImageItem } from "./ImageItem";

const itemStructure: ImageItem = {
  type: "image",
  id: -1,
  createdAt: -1,
  uid: "image_-1",

  data: null,
  mediaUUID: null,
  height: 0,
  width: 0,
  aspectRatio: 1,
  objectFit: "cover",
  repeat: "no-repeat",
  mediaType: "image"
};

export const useImageStore = defineItemStore("model/images", itemStructure, {
  getters: {
    /**
     * The actual image data of the item
     */
    mediaData(state) {
      const mediaData = useMediaStore().data;
      const entries: { [imageId: number]: string | null } = {};
      for (const id in state.items) {
        const uuid = state.items[id].mediaUUID;
        if (uuid == null) {
          entries[id] = null;
        } else {
          entries[id] = mediaData?.[uuid];
        }
      }
      return entries;
    },
    linkedMediaUUIDs(state) {
      return new Set(Object.values(state.items)
        .map((image) => image.mediaUUID)
        .filter((uuid): uuid is string => uuid != null));
    }
  },
  events: {
    beforeUpdate(updatedImage, changedData, imageBefore) {
      // if the image data changed, create a new media entry
      // to save that image.
      if (updatedImage.data) {
        // Remove the old media entry
        if (imageBefore && imageBefore.mediaUUID) {
          // does any other image still use this medium?
          if (!some(this.items, (image) => {
            return image.mediaUUID === imageBefore.mediaUUID && image.id !== updatedImage.id;
          })) {
            // if not, delete the medium
            useMediaStore().delete({ uuid: imageBefore.mediaUUID });
          }
        }

        // create the new media entry and connect the image to it
        const newMediaItem = useMediaStore().create({
          data: updatedImage.data
        });

        return { data: null, mediaUUID: newMediaItem.uuid };
      }

      return updatedImage;
    },
    async afterDelete(deletedImage) {
      if (deletedImage.mediaUUID) {
        if (!this.linkedMediaUUIDs.has(deletedImage.mediaUUID)) {
          useMediaStore().delete({ uuid: deletedImage.mediaUUID });
        } else {
          console.log("don't delete medium, still in use");
        }
      }
    }
  },
  actions: {
    /**
     * Create media store entries for all images.
     * If an image has data, that data is moved to the media store.
     */
    async createMediaEntries() {
      const mediaStore = useMediaStore();

      for (const imageId in this.items) {
        const image = this.items[imageId];

        if (image.mediaUUID && image.mediaUUID in mediaStore.items) {
          // do nothing, the media entry exists.
          continue;
        }

        try {
          if (image.data) {
            const mediaEntry = mediaStore.create({ data: image.data });
            image.data = null;
            image.mediaUUID = mediaEntry.uuid;
          } else if (image.mediaUUID) {
            mediaStore.add({ ...image, uuid: image.mediaUUID });
          } else {
            throw new Error("Image has no data and no mediaUUID.");
          }
        } catch (error) {
          console.log("Error during media init.");
        }
      }
    },

    /**
     * Sets all the media references to new uuids,
     * making sure that all images are uploaded as new
     * (necessary when importing a model)
     */
    async markImagesAsNew() {
      const mediaStore = useMediaStore();

      for (const imageId in this.items) {
        const image = this.items[imageId];
        if (!image.mediaUUID) continue;

        try {
          const newMedium = mediaStore.recreate(image.mediaUUID);
          image.mediaUUID = newMedium.uuid;
        } catch (error) {
          console.warn(`Could not recreate medium for image ${imageId}, ${image.mediaUUID}, skipping.`, error);
          await this.delete({ id: parseInt(imageId) });
        }
      }
    },

    async duplicate(imageId: number) {
      const image = this.items[imageId];
      if (image.mediaUUID == null) {
        throw new Error("Image has no mediaUUID");
      }
      const mediaUUID = image.mediaUUID;
      const newImage = await this.create({
        ...image,
        mediaUUID: mediaUUID
      });
      return newImage;
    }
  }
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHotItemStore(useImageStore, import.meta.hot));
}
